/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { FaBook, FaPlus, FaTrash, FaEdit, FaTimes, FaCalendarAlt, FaInfoCircle, FaLanguage, FaTimesCircle, FaTrophy, FaGraduationCap, FaBriefcase, FaChevronDown } from 'react-icons/fa';
import * as authService from '../services/authService';
import * as masterService from '../services/masterService';
import * as adminService from '../services/adminService';

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 z-[200] px-6 py-3 rounded-lg shadow-lg ${type === 'success' ? 'bg-green-500' : 'bg-red-500'
      } text-white font-semibold`}>
      {message}
    </div>
  );
};

const CreateArticleTitle = () => {
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchSubject, setSearchSubject] = useState('');
  const [searchStream, setSearchStream] = useState('');
  const [searchSpecialization, setSearchSpecialization] = useState('');
  const [searchLanguage, setSearchLanguage] = useState('');
  const [yiPaymentSettings, setYiPaymentSettings] = useState({ enabled: false, price: 0 });

  // Dropdown visibility states
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [showStreamDropdown, setShowStreamDropdown] = useState(false);
  const [showSpecializationDropdown, setShowSpecializationDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Refs for dropdown containers
  const subjectRef = useRef(null);
  const streamRef = useRef(null);
  const specializationRef = useRef(null);
  const languageRef = useRef(null);

  // Load data from localStorage
  const [subjects, setSubjects] = useState([]);
  const [streams, setStreams] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [languages, setLanguages] = useState([]);

  useEffect(() => {
    const init = async () => {
      const data = await loadExternalData();
      await loadData(data);
      try {
        const response = await adminService.getYIArticlePaymentSettings();
        if (response && response.success && response.data && response.data.settings) {
          setYiPaymentSettings({
            enabled: response.data.settings.yi_article_payment_enabled === 'true',
            price: response.data.settings.yi_article_price || 0
          });
        }
      } catch (err) {
        console.error('Error fetching YI settings:', err);
      }
    };
    init();

    const currentUser = authService.getCurrentUser();
    if (!currentUser || currentUser.role !== 'admin') {
      console.warn('User not authenticated or not admin');
    }
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subjectRef.current && !subjectRef.current.contains(event.target)) {
        setShowSubjectDropdown(false);
      }
      if (streamRef.current && !streamRef.current.contains(event.target)) {
        setShowStreamDropdown(false);
      }
      if (specializationRef.current && !specializationRef.current.contains(event.target)) {
        setShowSpecializationDropdown(false);
      }
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadExternalData = React.useCallback(async () => {
    try {
      const [subjectsData, streamsData, specializationsData, languagesData] = await Promise.all([
        masterService.getAllSubjects(),
        masterService.getAllStreams(),
        masterService.getAllSpecializations(),
        masterService.getAllLanguages()
      ]);

      const s = subjectsData.success ? subjectsData.data : (Array.isArray(subjectsData) ? subjectsData : []);
      const m = streamsData.success ? streamsData.data : (Array.isArray(streamsData) ? streamsData : []);
      const sp = specializationsData.success ? specializationsData.data : (Array.isArray(specializationsData) ? specializationsData : []);
      const l = languagesData.success ? languagesData.data : (Array.isArray(languagesData) ? languagesData : []);

      setSubjects(s);
      setStreams(m);
      setSpecializations(sp);
      setLanguages(l);

      return { subjects: s, streams: m, specializations: sp, languages: l };

    } catch (error) {
      console.error('Error loading external data:', error);
      showToast('Failed to load master data', 'error');
      return { subjects: [], majors: [], specializations: [], languages: [] };
    }
  }, []);

  const loadData = React.useCallback(async (externalData = null) => {
    try {
      const response = await adminService.getAllRunningTitles();
      let titles = [];

      if (response && response.success && Array.isArray(response.data)) {
        titles = response.data;
      } else if (Array.isArray(response)) {
        titles = response;
      }

      const currentLanguages = externalData ? externalData.languages : languages;
      const currentSubjects = externalData ? externalData.subjects : subjects;
      const currentStreams = externalData ? externalData.streams : streams;
      const currentSpecs = externalData ? externalData.specializations : specializations;

      const mappedTitles = titles.map(title => {
        // Resolve Languages
        let resolvedLanguages = [];
        let rawLanguages = title.languages || title.language_ids || title.languages_allowed || [];

        // Handle stringified JSON from backend (as seen in Postman)
        if (typeof rawLanguages === 'string') {
          try {
            rawLanguages = JSON.parse(rawLanguages);
          } catch (e) {
            console.error('Error parsing languages:', e);
            rawLanguages = [];
          }
        }

        if (Array.isArray(rawLanguages)) {
          resolvedLanguages = rawLanguages.map(l => {
            if (typeof l === 'object') return l.name || l.label;
            // Check if it's an ID/code in our master data
            const lang = currentLanguages.find(langObj =>
              (langObj.id === l || langObj._id === l) ||
              (langObj.code && String(langObj.code).toLowerCase() === String(l).toLowerCase()) ||
              (langObj.name && String(langObj.name).toLowerCase() === String(l).toLowerCase())
            );
            return lang ? lang.name : l;
          }).filter(name => name);
        }

        // Resolve Subject Name if it's an ID
        let subjectName = title.subject_name;
        if (!subjectName && title.subject) {
          if (typeof title.subject === 'object') {
            subjectName = title.subject.name;
          } else {
            const subjectObj = currentSubjects.find(s => s.id === title.subject || s._id === title.subject);
            subjectName = subjectObj ? subjectObj.name : title.subject;
          }
        } else if (!subjectName && title.subject_id) {
          const subjectObj = currentSubjects.find(s => s.id === title.subject_id || s._id === title.subject_id);
          subjectName = subjectObj ? subjectObj.name : title.subject_id;
        }

        // Resolve Stream Name if it's an ID
        let streamName = title.stream_name;
        if (!streamName && title.stream) {
          const val = title.stream;
          if (typeof val === 'object') {
            streamName = val.name;
          } else {
            const streamObj = currentStreams.find(m => m.id === val || m._id === val);
            streamName = streamObj ? streamObj.name : val;
          }
        } else if (!streamName && title.stream_id) {
          const valId = title.stream_id;
          const streamObj = currentStreams.find(m => m.id === valId || m._id === valId);
          streamName = streamObj ? streamObj.name : valId;
        }

        // Resolve Specialization Name if it's an ID
        let specName = title.specialization_name;
        if (!specName && title.specialization) {
          if (typeof title.specialization === 'object') {
            specName = title.specialization.name;
          } else {
            const specObj = currentSpecs.find(s => s.id === title.specialization || s._id === title.specialization);
            specName = specObj ? specObj.name : title.specialization;
          }
        } else if (!specName && title.specialization_id) {
          const specObj = currentSpecs.find(s => s.id === title.specialization_id || s._id === title.specialization_id);
          specName = specObj ? specObj.name : title.specialization_id;
        }

        // Resolve Created By - handle various possible structures
        let creatorName = 'Admin';
        if (title.creator) {
          creatorName = title.creator.full_name || title.creator.name || `${title.creator.firstName || ''} ${title.creator.lastName || ''}`.trim() || 'Admin';
        } else if (title.user) {
          creatorName = title.user.full_name || title.user.name || `${title.user.firstName || ''} ${title.user.lastName || ''}`.trim() || 'Admin';
        } else if (typeof title.created_by === 'object' && title.created_by) {
          creatorName = title.created_by.full_name || title.created_by.name || `${title.created_by.firstName || ''} ${title.created_by.lastName || ''}`.trim() || 'Admin';
        } else if (title.created_by) {
          creatorName = title.created_by.length > 20 ? 'Admin' : title.created_by;
        }

        return {
          ...title,
          id: title.id || title._id,
          articleTitle: title.title || title.articleTitle,
          subject: subjectName || 'N/A',
          stream: streamName || '',
          specialization: specName || '',
          submissionDeadline: title.submission_deadline || title.submissionDeadline,
          resultDate: title.result_date || title.resultDate,
          createdAt: title.created_at || title.createdAt || title.date,
          createdBy: creatorName,
          languages: resolvedLanguages,
          price: title.price || 0
        };
      });

      setArticleTitles(mappedTitles);
    } catch (error) {
      console.error('Error loading article titles:', error);
      showToast('Failed to load article titles', 'error');
    }
  }, [languages, streams, specializations, subjects]);

  const [formData, setFormData] = useState({
    articleTitle: '',
    subject: '',
    stream: '',
    specialization: '',
    languages: [],
  });

  const [deadlineDate, setDeadlineDate] = useState('');
  const [resultDate, setResultDate] = useState('');
  const [deadlineEnabled, setDeadlineEnabled] = useState(false);
  const [resultEnabled, setResultEnabled] = useState(false);

  // Helper functions for ID resolution
  const getSubjectIdByName = (name) => {
    const s = subjects.find(sub => sub.name === name);
    return s ? (s.id || s._id) : null;
  };

  const getStreamIdByName = (name) => {
    const m = streams.find(st => st.name === name);
    return m ? (m.id || m._id) : null;
  };

  const selectedSubjectId = getSubjectIdByName(formData.subject);
  const selectedStreamId = getStreamIdByName(formData.stream);

  // Filter functions for search with cascading logic
  const filteredSubjects = subjects.filter(subject =>
    subject.name.toLowerCase().includes(searchSubject.toLowerCase())
  );

  const filteredStreams = streams.filter(stream => {
    const matchesSearch = stream.name.toLowerCase().includes(searchStream.toLowerCase());
    // Filter by Subject if selected
    const streamSubjectId = stream.subject_id || (stream.subject ? (stream.subject.id || stream.subject._id) : null);
    const matchesSubject = !selectedSubjectId || (streamSubjectId === selectedSubjectId);

    return matchesSearch && matchesSubject;
  });

  const filteredSpecializations = specializations.filter(spec => {
    const matchesSearch = spec.name.toLowerCase().includes(searchSpecialization.toLowerCase());

    // Filter by Stream if selected
    const specStreamId = spec.stream_id || (spec.stream ? (spec.stream.id || spec.stream._id) : null);
    const matchesStream = !selectedStreamId || (specStreamId === selectedStreamId);

    // If Stream not selected but Subject IS, filter by Subject (if spec has subject_id)
    const specSubjectId = spec.subject_id || (spec.subject ? (spec.subject.id || spec.subject._id) : null);
    const matchesSubject = !selectedSubjectId || (specSubjectId === selectedSubjectId);

    if (selectedStreamId) return matchesSearch && matchesStream;
    if (selectedSubjectId) return matchesSearch && matchesSubject;

    return matchesSearch;
  });

  const filteredLanguages = languages.filter(lang =>
    lang.name.toLowerCase().includes(searchLanguage.toLowerCase()) ||
    (lang.nativeName && lang.nativeName.toLowerCase().includes(searchLanguage.toLowerCase()))
  );



  const [articleTitles, setArticleTitles] = useState([]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleRemoveLanguage = (languageToRemove) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.filter(lang => lang !== languageToRemove)
    }));
  };

  const handleSelectLanguage = (langName) => {
    if (!formData.languages.includes(langName)) {
      setFormData(prev => ({
        ...prev,
        languages: [...prev.languages, langName]
      }));
      setSearchLanguage('');
      setShowLanguageDropdown(false);
    }
  };

  const validateForm = () => {
    if (!formData.articleTitle.trim()) {
      showToast('Please enter article title', 'error');
      return false;
    }
    if (!formData.subject) {
      showToast('Please select a department', 'error');
      return false;
    }
    if (!formData.stream) {
      showToast('Please select a stream', 'error');
      return false;
    }

    if (formData.languages.length === 0) {
      showToast('Please add at least one language', 'error');
      return false;
    }

    // Validate that result date is after deadline date if both are set
    if (deadlineDate && resultDate) {
      const deadline = new Date(deadlineDate);
      const result = new Date(resultDate);
      if (result <= deadline) {
        showToast('Result date must be after submission deadline', 'error');
        return false;
      }
    }

    return true;
  };

  const openCreateModal = () => {
    setFormData({
      articleTitle: '',
      subject: '',
      stream: '',
      specialization: '',
      languages: [],
    });
    setDeadlineDate('');
    setResultDate('');
    setDeadlineEnabled(false);
    setResultEnabled(false);
    setEditingId(null);
    setSearchSubject('');
    setSearchStream('');
    setSearchSpecialization('');
    setSearchLanguage('');
    setShowSubjectDropdown(false);
    setShowStreamDropdown(false);
    setShowSpecializationDropdown(false);
    setShowLanguageDropdown(false);
    setShowModal(true);
  };

  const openEditModal = async (inputTitle) => {
    try {
      let title = inputTitle;
      try {
        const titleId = inputTitle.id || inputTitle._id;
        const response = await adminService.getRunningTitleById(titleId);
        if (response.success) {
          title = response.data;
        }
      } catch (error) {
        console.error('Error fetching title details:', error);
        // Fallback to inputTitle if fetch fails
      }

      // Map title data to form format with normalization
      const normalizedTitle = {
        id: title.id || title._id,
        articleTitle: title.title || title.articleTitle || '',
        specialization: '',
        languages: [],
        price: 0
      };

      // Resolve Subject
      if (title.subject) {
        normalizedTitle.subject = typeof title.subject === 'object' ? title.subject.name :
          (subjects.find(s => s.id === title.subject || s._id === title.subject)?.name || title.subject);
      } else if (title.subject_id) {
        normalizedTitle.subject = subjects.find(s => s.id === title.subject_id || s._id === title.subject_id)?.name || title.subject_id;
      }

      // Resolve Stream
      const streamVal = title.stream;
      const streamValId = title.stream_id;
      if (streamVal) {
        normalizedTitle.stream = typeof streamVal === 'object' ? streamVal.name :
          (streams.find(m => m.id === streamVal || m._id === streamVal)?.name || streamVal);
      } else if (streamValId) {
        normalizedTitle.stream = streams.find(m => m.id === streamValId || m._id === streamValId)?.name || streamValId;
      }

      // Resolve Specialization
      if (title.specialization) {
        normalizedTitle.specialization = typeof title.specialization === 'object' ? title.specialization.name :
          (specializations.find(s => s.id === title.specialization || s._id === title.specialization)?.name || title.specialization);
      } else if (title.specialization_id) {
        normalizedTitle.specialization = specializations.find(s => s.id === title.specialization_id || s._id === title.specialization_id)?.name || title.specialization_id;
      }

      // Resolve Languages
      let rawLangs = title.languages || title.language_ids || title.languages_allowed || [];
      if (typeof rawLangs === 'string') {
        try {
          rawLangs = JSON.parse(rawLangs);
        } catch (e) {
          rawLangs = [];
        }
      }

      if (Array.isArray(rawLangs)) {
        normalizedTitle.languages = rawLangs.map(l => {
          if (typeof l === 'object') return l.name || l.label;
          const lang = languages.find(langObj =>
            (langObj.id === l || langObj._id === l) ||
            (langObj.code && String(langObj.code).toLowerCase() === String(l).toLowerCase()) ||
            (langObj.name && String(langObj.name).toLowerCase() === String(l).toLowerCase())
          );
          return lang ? lang.name : l;
        }).filter(n => n);
      }

      setFormData({
        articleTitle: normalizedTitle.articleTitle || '',
        subject: normalizedTitle.subject || '',
        stream: normalizedTitle.stream || '',
        specialization: normalizedTitle.specialization || '',
        languages: normalizedTitle.languages || [],
      });

      // Parse deadline date if exists
      let deadlineDateValue = '';
      const rawDeadline = title.submission_deadline || title.submissionDeadline;
      if (rawDeadline) {
        const deadline = new Date(rawDeadline);
        const year = deadline.getFullYear();
        const month = String(deadline.getMonth() + 1).padStart(2, '0');
        const day = String(deadline.getDate()).padStart(2, '0');
        deadlineDateValue = `${year}-${month}-${day}`;
      }

      // Parse result date if exists
      let resultDateValue = '';
      const rawResult = title.result_date || title.resultDate;
      if (rawResult) {
        const result = new Date(rawResult);
        const year = result.getFullYear();
        const month = String(result.getMonth() + 1).padStart(2, '0');
        const day = String(result.getDate()).padStart(2, '0');
        resultDateValue = `${year}-${month}-${day}`;
      }

      setDeadlineDate(deadlineDateValue);
      setResultDate(resultDateValue);
      setDeadlineEnabled(!!deadlineDateValue);
      setResultEnabled(!!resultDateValue);
      setEditingId(title.id || title._id);
      setSearchSubject('');
      setSearchStream('');
      setSearchSpecialization('');
      setSearchLanguage('');
      setShowSubjectDropdown(false);
      setShowStreamDropdown(false);
      setShowSpecializationDropdown(false);
      setShowLanguageDropdown(false);
      setShowModal(true);
    } catch (error) {
      console.error('Error opening edit modal:', error);
      showToast('Failed to load title details', 'error');
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    const currentUser = authService.getCurrentUser();

    try {
      // Helper functions to resolve IDs from names
      const getSubjectId = (name) => {
        const item = subjects.find(s => s.name === name);
        return item ? (item.id || item._id) : null;
      };

      const getStreamId = (name) => {
        const item = streams.find(m => m.name === name);
        return item ? (item.id || item._id) : null;
      };

      const getSpecializationId = (name) => {
        const item = specializations.find(s => s.name === name);
        return item ? (item.id || item._id) : null;
      };

      const getLanguageIds = (names) => names.map(name => {
        const item = languages.find(l => (l.name === name || l.nativeName === name));
        // Return code (like 'en', 'hi') if available, falling back to id
        return item ? (item.code || item.id || item._id) : null;
      }).filter(id => id);

      const subjectId = getSubjectId(formData.subject);
      const streamId = getStreamId(formData.stream);
      const specializationId = getSpecializationId(formData.specialization);
      const languageCodes = getLanguageIds(formData.languages);

      if (!subjectId) {
        showToast('Invalid Department selected', 'error');
        setIsSubmitting(false);
        return;
      }

      // Build payload - only include optional fields if they have values
      const payload = {
        title: formData.articleTitle,
        subject_id: subjectId,
        language_ids: languageCodes,
        languages_allowed: languageCodes, // Send as array of codes like ['en', 'hi']
        status: 'ACTIVE'
      };

      // Only add stream_id if it exists (backend expects string, not null)
      if (streamId) {
        payload.stream_id = streamId;
      }

      // Only add specialization_id if it exists (backend expects string, not null)
      if (specializationId) {
        payload.specialization_id = specializationId;
      }

      // Only add submission_deadline if enabled and has a value
      if (deadlineEnabled && deadlineDate) {
        payload.submission_deadline = deadlineDate;
      }

      // Only add result_date if enabled and has a value
      if (resultEnabled && resultDate) {
        payload.result_date = resultDate;
      }

      if (editingId) {
        // Update existing title
        await adminService.updateRunningTitle(editingId, payload);
        showToast('Article title updated successfully!', 'success');
      } else {
        // Create new title
        await adminService.createRunningTitle(payload);
        showToast('Article title created successfully!', 'success');
      }

      // Refresh the list to show the new/updated title
      await loadData(); // Refresh list

      // Close modal and reset form
      setShowModal(false);
      setIsSubmitting(false);

    } catch (error) {
      console.error('Error saving article title:', error);
      showToast(error.message || 'Error saving article title. Please try again.', 'error');
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this article title?')) {
      try {
        await adminService.deleteRunningTitle(id);
        showToast('Article title deleted successfully!', 'success');
        await loadData(); // Refresh list

        if (editingId === id) {
          setShowModal(false);
        }
      } catch (error) {
        console.error('Error deleting article title:', error);
        showToast(error.message || 'Failed to delete article title', 'error');
      }
    }
  };

  const isDeadlinePassed = (deadline) => {
    if (!deadline) return false;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const deadlineDateOnly = new Date(deadlineDate.getFullYear(), deadlineDate.getMonth(), deadlineDate.getDate());
    const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return deadlineDateOnly < nowDateOnly;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not Set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysRemaining = (deadline) => {
    if (!deadline) return null;
    const now = new Date();
    const deadlineDate = new Date(deadline);

    const deadlineStart = new Date(deadlineDate.getFullYear(), deadlineDate.getMonth(), deadlineDate.getDate());
    const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffTime = deadlineStart - nowStart;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const inputClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-gray-300 text-base";
  const labelClass = "block text-base font-semibold text-gray-700 mb-2";

  const darkGradient = {
    background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Unified Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header - NOT sticky anymore */}
            <div className="p-6" style={darkGradient}>
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    {editingId ? 'Edit Article Title' : 'Create New Article Title'}
                  </h2>
                  <p className="text-blue-100">Create title, specify languages, and set submission & result dates</p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body - Now scrollable */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-6">
                <div className="space-y-6">
                  {/* Basic Information Section */}
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-xl border-2 border-blue-200 mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <FaBook className="text-blue-600" />
                      Basic Information
                    </h3>

                    {/* Article Title - MOVED TO FIRST POSITION */}
                    <div className="mb-4">
                      <label className={labelClass}>
                        Article Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="articleTitle"
                        value={formData.articleTitle}
                        onChange={handleFormChange}
                        required
                        className={inputClass}
                        placeholder="Enter article title (e.g., 'The Future of Artificial Intelligence')"
                      />
                    </div>

                    {/* Subject Dropdown with Search */}
                    <div className="mb-4" ref={subjectRef}>
                      <label className={labelClass}>
                        Department <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={searchSubject}
                          onChange={(e) => {
                            setSearchSubject(e.target.value);
                            setShowSubjectDropdown(true);
                          }}
                          onFocus={() => setShowSubjectDropdown(true)}
                          placeholder="Search or type department..."
                          className={`${inputClass} pr-10`}
                        />
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                          {searchSubject && (
                            <button
                              type="button"
                              onClick={() => {
                                setSearchSubject('');
                                setShowSubjectDropdown(false);
                              }}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <FaTimes />
                            </button>
                          )}
                          <FaChevronDown className="text-gray-400" />
                        </div>
                      </div>

                      {/* Subject Dropdown */}
                      {showSubjectDropdown && filteredSubjects.length > 0 && (
                        <div className="mt-1 border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg z-10">
                          {filteredSubjects.map(subject => (
                            <button
                              key={subject.id}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, subject: subject.name, stream: '', specialization: '' });
                                setSearchSubject('');
                                setShowSubjectDropdown(false);
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 flex items-start gap-3"
                            >
                              <FaBook className="text-blue-500 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{subject.name}</div>
                                {subject.description && (
                                  <p className="text-xs text-gray-500 mt-1 truncate">{subject.description}</p>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Selected Subject Display */}
                      {formData.subject && (
                        <div className="mt-2 flex items-center justify-between px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center gap-2">
                            <FaBook className="text-blue-500" />
                            <span className="font-normal text-blue-700 text-sm">{formData.subject}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, subject: '' })}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Stream Dropdown with Search */}
                    <div className="mb-4" ref={streamRef}>
                      <label className={labelClass}>
                        Stream <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={searchStream}
                          onChange={(e) => {
                            setSearchStream(e.target.value);
                            setShowStreamDropdown(true);
                          }}
                          onFocus={() => setShowStreamDropdown(true)}
                          placeholder="Search or type stream..."
                          className={`${inputClass} pr-10`}
                        />
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                          {searchStream && (
                            <button
                              type="button"
                              onClick={() => {
                                setSearchStream('');
                                setShowStreamDropdown(false);
                              }}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <FaTimes />
                            </button>
                          )}
                          <FaChevronDown className="text-gray-400" />
                        </div>
                      </div>

                      {/* Stream Dropdown */}
                      {showStreamDropdown && filteredStreams.length > 0 && (
                        <div className="mt-1 border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg z-10">
                          {filteredStreams.map(stream => (
                            <button
                              key={stream.id}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, stream: stream.name, specialization: '' });
                                setSearchStream('');
                                setShowStreamDropdown(false);
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-purple-50 border-b border-gray-100 last:border-b-0 flex items-start gap-3"
                            >
                              <FaGraduationCap className="text-purple-500 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{stream.name}</div>
                                {stream.description && (
                                  <p className="text-xs text-gray-500 mt-1 truncate">{stream.description}</p>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Selected Stream Display */}
                      {formData.stream && (
                        <div className="mt-2 flex items-center justify-between px-3 py-2 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="flex items-center gap-2">
                            <FaGraduationCap className="text-purple-500" />
                            <span className="font-normal text-blue-700 text-sm">{formData.stream}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, stream: '' })}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Specialization Dropdown with Search */}
                    <div className="mb-4" ref={specializationRef}>
                      <label className={labelClass}>
                        Specialization (Optional)
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={searchSpecialization}
                          onChange={(e) => {
                            setSearchSpecialization(e.target.value);
                            setShowSpecializationDropdown(true);
                          }}
                          onFocus={() => setShowSpecializationDropdown(true)}
                          placeholder="Search or type specialization..."
                          className={`${inputClass} pr-10`}
                        />
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                          {searchSpecialization && (
                            <button
                              type="button"
                              onClick={() => {
                                setSearchSpecialization('');
                                setShowSpecializationDropdown(false);
                              }}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <FaTimes />
                            </button>
                          )}
                          <FaChevronDown className="text-gray-400" />
                        </div>
                      </div>

                      {/* Specialization Dropdown */}
                      {showSpecializationDropdown && filteredSpecializations.length > 0 && (
                        <div className="mt-1 border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg z-10">
                          {filteredSpecializations.map(spec => (
                            <button
                              key={spec.id}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, specialization: spec.name });
                                setSearchSpecialization('');
                                setShowSpecializationDropdown(false);
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-indigo-50 border-b border-gray-100 last:border-b-0 flex items-start gap-3"
                            >
                              <FaBriefcase className="text-indigo-500 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{spec.name}</div>
                                {spec.description && (
                                  <p className="text-xs text-gray-500 mt-1 truncate">{spec.description}</p>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Selected Specialization Display */}
                      {formData.specialization && (
                        <div className="mt-2 flex items-center justify-between px-3 py-2 bg-indigo-50 rounded-lg border border-indigo-200">
                          <div className="flex items-center gap-2">
                            <FaBriefcase className="text-indigo-500" />
                            <span className="font-normal text-blue-700 text-sm">{formData.specialization}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, specialization: '' })}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Languages Section */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border-2 border-green-200 mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <FaLanguage className="text-green-600" />
                      Languages <span className="text-red-500">*</span>
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Students can submit articles in these languages. Search and select from created languages.
                    </p>

                    {/* Language Search */}
                    <div className="mb-4" ref={languageRef}>
                      <label className="block text-base font-semibold text-gray-700 mb-2">
                        Search Languages
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={searchLanguage}
                          onChange={(e) => {
                            setSearchLanguage(e.target.value);
                            setShowLanguageDropdown(true);
                          }}
                          onFocus={() => setShowLanguageDropdown(true)}
                          placeholder="Type to search languages..."
                          className={`${inputClass} pr-10`}
                        />
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                          {searchLanguage && (
                            <button
                              type="button"
                              onClick={() => {
                                setSearchLanguage('');
                                setShowLanguageDropdown(false);
                              }}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <FaTimes />
                            </button>
                          )}
                          <FaChevronDown className="text-gray-400" />
                        </div>
                      </div>

                      {/* Language Search Results Dropdown */}
                      {showLanguageDropdown && filteredLanguages.length > 0 && (
                        <div className="mt-1 border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg z-10">
                          {filteredLanguages.map(lang => (
                            <button
                              key={lang.id}
                              type="button"
                              onClick={() => handleSelectLanguage(lang.name)}
                              disabled={formData.languages.includes(lang.name)}
                              className={`w-full px-4 py-3 text-left border-b border-gray-100 last:border-b-0 flex items-start gap-3 ${formData.languages.includes(lang.name)
                                ? 'bg-green-100'
                                : 'hover:bg-green-50'
                                }`}
                            >
                              <FaLanguage className={`mt-0.5 flex-shrink-0 ${formData.languages.includes(lang.name) ? 'text-green-600' : 'text-green-500'
                                }`} />
                              <div className="flex-1">
                                <div className={`font-medium ${formData.languages.includes(lang.name) ? 'text-green-700' : 'text-gray-900'
                                  }`}>
                                  {lang.name}
                                  {formData.languages.includes(lang.name) && (
                                    <span className="ml-2 text-green-600 text-sm">✓ Added</span>
                                  )}
                                </div>
                                {lang.nativeName && lang.nativeName !== lang.name && (
                                  <p className="text-xs text-gray-500 mt-1">Native: {lang.nativeName}</p>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Selected Languages Chips */}
                    {formData.languages.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <FaLanguage className="text-green-500" />
                            <span className="text-base font-semibold text-gray-700">
                              Selected Languages ({formData.languages.length})
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">Click × to remove</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {formData.languages.map((lang, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-2 px-3 py-2 bg-white border-2 border-green-300 rounded-lg shadow-sm"
                            >
                              <FaLanguage className="text-green-500" />
                              <span className="text-sm font-semibold text-green-800">{lang}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveLanguage(lang)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                                title="Remove language"
                              >
                                <FaTimesCircle />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Deadline Section */}
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-xl border-2 border-orange-200 mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <FaCalendarAlt className="text-orange-600" />
                      Submission Deadline (Optional)
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Set a deadline date for article submissions. If no deadline is set, students can submit anytime.
                    </p>

                    <div className="flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-300">
                      <span className="text-base font-semibold text-gray-700">Set Deadline</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={deadlineEnabled}
                          onChange={(e) => {
                            setDeadlineEnabled(e.target.checked);
                            if (!e.target.checked) {
                              setDeadlineDate('');
                            } else {
                              const tomorrow = new Date();
                              tomorrow.setDate(tomorrow.getDate() + 1);
                              const year = tomorrow.getFullYear();
                              const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
                              const day = String(tomorrow.getDate()).padStart(2, '0');
                              setDeadlineDate(`${year}-${month}-${day}`);
                            }
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                      </label>
                    </div>

                    {deadlineEnabled && (
                      <>
                        <div className="mb-4">
                          <label className="block text-base font-semibold text-gray-700 mb-2">Deadline Date</label>
                          <input
                            type="date"
                            value={deadlineDate}
                            onChange={(e) => setDeadlineDate(e.target.value)}
                            className={inputClass}
                            min={new Date().toISOString().split('T')[0]}
                          />
                        </div>

                        <div className="mt-4 p-3 bg-gradient-to-r from-orange-100 to-amber-100 rounded-lg border border-orange-300">
                          <div className="flex items-center gap-2 mb-1">
                            <FaCalendarAlt className="text-orange-600" />
                            <span className="text-sm font-semibold text-gray-800">Deadline:</span>
                          </div>
                          <p className="text-lg font-bold text-orange-700">
                            {deadlineDate ? new Date(deadlineDate).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            }) : 'Please select a date'}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            Submissions accepted until end of day (11:59 PM)
                          </p>
                        </div>
                      </>
                    )}

                    {!deadlineEnabled && (
                      <div className="mt-4 p-3 bg-gray-100 rounded-lg border border-gray-300">
                        <p className="text-sm font-semibold text-gray-600">
                          <span className="text-blue-500">ℹ</span> No deadline set. Students can submit articles anytime.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Result Date Section */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border-2 border-purple-200 mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <FaTrophy className="text-purple-600" />
                      Result Announcement Date (Optional)
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Set the date when results will be announced. Students will see this date in their submissions.
                    </p>

                    <div className="flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-300">
                      <span className="text-base font-semibold text-gray-700">Set Result Date</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={resultEnabled}
                          onChange={(e) => {
                            setResultEnabled(e.target.checked);
                            if (!e.target.checked) {
                              setResultDate('');
                            } else {
                              const nextWeek = new Date();
                              nextWeek.setDate(nextWeek.getDate() + 7);
                              const year = nextWeek.getFullYear();
                              const month = String(nextWeek.getMonth() + 1).padStart(2, '0');
                              const day = String(nextWeek.getDate()).padStart(2, '0');
                              setResultDate(`${year}-${month}-${day}`);
                            }
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                      </label>
                    </div>

                    {resultEnabled && (
                      <>
                        <div className="mb-4">
                          <label className="block text-base font-semibold text-gray-700 mb-2">Result Date</label>
                          <input
                            type="date"
                            value={resultDate}
                            onChange={(e) => setResultDate(e.target.value)}
                            className={inputClass}
                            min={deadlineDate || new Date().toISOString().split('T')[0]}
                          />
                          {deadlineDate && (
                            <p className="text-xs text-gray-500 mt-1">
                              Must be after submission deadline ({formatDate(deadlineDate)})
                            </p>
                          )}
                        </div>

                        <div className="mt-4 p-3 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg border border-purple-300">
                          <div className="flex items-center gap-2 mb-1">
                            <FaTrophy className="text-purple-600" />
                            <span className="text-sm font-semibold text-gray-800">Result Date:</span>
                          </div>
                          <p className="text-lg font-bold text-purple-700">
                            {resultDate ? new Date(resultDate).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            }) : 'Please select a date'}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            Results will be announced on this date
                          </p>
                        </div>
                      </>
                    )}

                    {!resultEnabled && (
                      <div className="mt-4 p-3 bg-gray-100 rounded-lg border border-gray-300">
                        <p className="text-sm font-semibold text-gray-600">
                          <span className="text-blue-500">ℹ</span> No result date set. Students won't see an announcement date.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Summary Section */}
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-xl border-2 border-indigo-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <FaInfoCircle className="text-indigo-600" />
                      Summary
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Article Title:</span>
                        <span className="text-sm font-semibold text-gray-900">{formData.articleTitle || 'Not set'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Subject:</span>
                        <span className="text-sm font-semibold text-gray-900">{formData.subject || 'Not set'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Stream:</span>
                        <span className="text-sm font-semibold text-gray-900">{formData.stream || 'Not set'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Specialization:</span>
                        <span className="text-sm font-semibold text-gray-900">{formData.specialization || 'Not set'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Languages:</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formData.languages.length > 0
                            ? `${formData.languages.length} language${formData.languages.length !== 1 ? 's' : ''}`
                            : 'Not set'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Submission Deadline:</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {deadlineDate
                            ? `${new Date(deadlineDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}`
                            : 'No deadline'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Price:</span>
                        <span className={`text-sm font-semibold ${yiPaymentSettings.enabled && yiPaymentSettings.price > 0 ? 'text-green-600' : 'text-gray-900'}`}>
                          {yiPaymentSettings.enabled && yiPaymentSettings.price > 0 ? `₹${yiPaymentSettings.price}` : 'Free'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Result Date:</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {resultDate
                            ? `${new Date(resultDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}`
                            : 'Not set'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:from-green-600 hover:to-emerald-600 transition-all disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        {editingId ? 'Updating...' : 'Creating...'}
                      </div>
                    ) : editingId ? 'Update Article Title' : 'Create Article Title'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-6">
        <div className="max-w mx-auto px-4">
          {/* Header */}
          <div className="rounded-2xl shadow-lg p-6 mb-6 relative overflow-hidden" style={darkGradient}>
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">Article Title Management</h1>
                <p className="text-blue-100 text-sm">Create and configure article titles with multiple languages for Young India submissions</p>
              </div>
              <div className="hidden md:block">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <FaLanguage className="text-3xl text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div className="flex justify-between items-center mb-6">
            <div className="text-gray-700">
              <h2 className="text-lg font-semibold">Manage Article Titles</h2>
              <p className="text-sm text-gray-600">Total: {articleTitles.length} titles</p>
            </div>
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all shadow-md font-semibold"
            >
              <FaPlus />
              Create New Article Title
            </button>
          </div>

          {/* Article Titles List */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-4" style={darkGradient}>
              <h2 className="text-xl font-bold text-white">Article Titles ({articleTitles.length})</h2>
            </div>

            <div className="p-6">
              {articleTitles.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaLanguage className="text-4xl text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-lg font-medium">No article titles created yet</p>
                  <button
                    onClick={openCreateModal}
                    className="mt-4 px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all font-semibold"
                  >
                    Create Your First Title
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr style={darkGradient}>
                        <th className="px-4 py-3 text-left text-sm font-bold text-white">#</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-white">Article Title</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-white">Department</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-white">Stream</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-white">Specialization</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-white">Languages</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-white">Submission Deadline</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-white">Result Date</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-white">Price</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-white">Created By</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-white">Created Date</th>
                        <th className="px-4 py-3 text-center text-sm font-bold text-white">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {articleTitles.map((title, index) => {
                        const daysRemaining = getDaysRemaining(title.submissionDeadline);
                        const deadlinePassed = isDeadlinePassed(title.submissionDeadline);

                        return (
                          <tr
                            key={title.id}
                            className="border-b hover:bg-purple-50/50 transition-colors"
                          >
                            <td className="px-4 py-4 text-sm text-gray-900 font-medium">{index + 1}</td>
                            <td className="px-4 py-4 text-sm text-gray-900 font-medium">{title.articleTitle}</td>
                            <td className="px-4 py-4">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                {title.subject}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              {title.stream ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                                  <FaGraduationCap className="mr-1" />
                                  {title.stream}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs">-</span>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              {title.specialization ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                                  <FaBriefcase className="mr-1" />
                                  {title.specialization}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs">-</span>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              {title.languages && title.languages.length > 0 ? (
                                <div className="flex flex-wrap gap-1 max-w-[300px]">
                                  {title.languages.slice(0, 3).map((lang, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-1 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-800 rounded-full text-xs font-medium"
                                    >
                                      {lang}
                                    </span>
                                  ))}
                                  {title.languages.length > 3 && (
                                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                                      +{title.languages.length - 3} more
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs italic">No languages set</span>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              {title.submissionDeadline ? (
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <FaCalendarAlt className={deadlinePassed ? 'text-red-500' : 'text-green-500'} />
                                    <span className={`text-xs font-semibold ${deadlinePassed ? 'text-red-700' : 'text-gray-900'}`}>
                                      {formatDate(title.submissionDeadline)}
                                    </span>
                                  </div>
                                  {deadlinePassed ? (
                                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                                      Expired
                                    </span>
                                  ) : daysRemaining !== null && (
                                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${daysRemaining <= 3 ? 'bg-red-100 text-red-700' :
                                      daysRemaining <= 7 ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-green-100 text-green-700'
                                      }`}>
                                      {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
                                  No Deadline
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              {title.resultDate ? (
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <FaTrophy className="text-purple-500" />
                                    <span className="text-xs font-semibold text-gray-900">
                                      {formatDate(title.resultDate)}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
                                  Not Set
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              {yiPaymentSettings.enabled && yiPaymentSettings.price > 0 ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                                  ₹{yiPaymentSettings.price}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-600">
                                  Free
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm font-semibold text-blue-600">
                              {title.createdBy}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              <div className="flex items-center gap-2">
                                <FaCalendarAlt className="text-gray-400" />
                                {title.createdAt ? new Date(title.createdAt).toLocaleDateString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                }) : 'Not available'}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => openEditModal(title)}
                                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all font-semibold text-sm"
                                >
                                  <FaEdit />
                                  Edit All
                                </button>
                                <button
                                  onClick={() => handleDelete(title.id || title._id)}
                                  className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                                  title="Delete"
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Statistics */}
          {articleTitles.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border-2 border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <FaLanguage className="text-white text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-semibold">Total Titles</p>
                    <p className="text-2xl font-bold text-gray-900">{articleTitles.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border-2 border-green-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <FaCalendarAlt className="text-white text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-semibold">Active Deadlines</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {articleTitles.filter(t => t.submissionDeadline && !isDeadlinePassed(t.submissionDeadline)).length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border-2 border-purple-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <FaTrophy className="text-white text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-semibold">With Result Dates</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {articleTitles.filter(t => t.resultDate).length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CreateArticleTitle;